import { useState } from "react";
import { MapPin, Phone, Mail, Clock3 } from "lucide-react";
import { SEDI, COMPANY } from "@/lib/data";
import SEO, { LOCATIONS_SCHEMA } from "@/components/SEO";
import QuoteForm from "@/components/QuoteForm";

export default function Contatti() {
    const [active, setActive] = useState(SEDI[0].slug);
    const sede = SEDI.find((s) => s.slug === active) || SEDI[0];

    return (
        <>
            <SEO
                title="Contatti · 4 Sedi in Ticino"
                description="Contatta 7MED: 4 sedi in Ticino (Mendrisio, Lugano-Savosa, Bellinzona, Locarno). Telefono +41 91 261 51 99 · Email backoffice@7med.ch · Risposta entro 24 ore."
                path="/contatti"
                keywords="7MED contatti, sedi Ticino, Mendrisio, Savosa, Bellinzona, Locarno, formazione primo soccorso contatti"
                structuredData={[
                    ...LOCATIONS_SCHEMA,
                    {
                        "@context": "https://schema.org",
                        "@type": "ContactPage",
                        name: "Contatti 7MED",
                        url: "https://7med.ch/contatti",
                    },
                ]}
                breadcrumbs={[{ name: "Home", path: "/" }, { name: "Contatti", path: "/contatti" }]}
            />
            <section className="bg-white">
                <div className="container-7m py-16 sm:py-20 lg:py-28">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12">
                        <div className="lg:col-span-7">
                            <div className="label-eyebrow text-brand">Contatti</div>
                            <h1 className="mt-4 font-heading font-black text-4xl sm:text-6xl lg:text-8xl tracking-tighter leading-[1.05]">
                                Siamo qui<br /><span className="text-brand">per ascoltarti.</span>
                            </h1>
                            <p className="mt-6 sm:mt-8 text-readable-lg max-w-2xl">
                                4 sedi in tutto il Ticino, una sola squadra. Scrivici, chiamaci o vieni a trovarci: rispondiamo a tutte le richieste entro 24 ore.
                            </p>
                        </div>
                        <div className="lg:col-span-4 lg:col-start-9 space-y-4">
                            <a href={`tel:${COMPANY.phoneRaw}`} className="block bg-ink text-white p-6 hover:bg-brand transition-colors group" data-testid="contatti-phone">
                                <Phone className="w-6 h-6 text-brand group-hover:text-white" />
                                <div className="mt-4 text-[10px] font-mono uppercase tracking-[0.2em] text-white/50">Telefono</div>
                                <div className="font-heading font-bold text-2xl mt-1">{COMPANY.phone}</div>
                            </a>
                            <a href={`mailto:${COMPANY.email}`} className="block bg-ink text-white p-6 hover:bg-brand transition-colors group" data-testid="contatti-email">
                                <Mail className="w-6 h-6 text-brand group-hover:text-white" />
                                <div className="mt-4 text-[10px] font-mono uppercase tracking-[0.2em] text-white/50">Email</div>
                                <div className="font-heading font-bold text-2xl mt-1 break-all">{COMPANY.email}</div>
                            </a>
                        </div>
                    </div>
                </div>
            </section>

            {/* SEDI selector */}
            <section className="py-16 lg:py-24 bg-[#FAFAFA] border-y border-ink/10">
                <div className="container-7m">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
                        <div className="lg:col-span-5">
                            <div className="label-eyebrow text-brand">Le nostre sedi</div>
                            <h2 className="mt-4 font-heading font-black text-4xl lg:text-5xl tracking-tighter leading-tight">4 sedi in Ticino.</h2>
                            <div className="mt-8 space-y-px bg-ink/10">
                                {SEDI.map((s) => (
                                    <button
                                        key={s.slug}
                                        onClick={() => setActive(s.slug)}
                                        className={`w-full text-left p-5 transition-all duration-200 group ${
                                            active === s.slug ? "bg-ink text-white" : "bg-white hover:bg-ink hover:text-white"
                                        }`}
                                        data-testid={`contatti-sede-tab-${s.slug}`}
                                    >
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <div className="font-heading font-bold text-xl">{s.city}</div>
                                                <div className={`text-sm mt-1 ${active === s.slug ? "text-white/60" : "text-ink-muted group-hover:text-white/60"}`}>
                                                    {s.address} · {s.zip}
                                                </div>
                                            </div>
                                            <MapPin className={`w-5 h-5 ${active === s.slug ? "text-brand" : "text-ink-muted group-hover:text-brand"}`} />
                                        </div>
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="lg:col-span-7 bg-white border border-ink/10">
                            <div className="aspect-[16/10] w-full bg-ink/5">
                                <iframe
                                    title={`Mappa ${sede.city}`}
                                    src={`https://www.google.com/maps?q=${sede.mapsQuery}&output=embed`}
                                    className="w-full h-full border-0"
                                    loading="lazy"
                                    referrerPolicy="no-referrer-when-downgrade"
                                    data-testid={`contatti-map-${sede.slug}`}
                                />
                            </div>
                            <div className="p-6 lg:p-8">
                                <div className="label-eyebrow">Sede di {sede.city}</div>
                                <h3 className="mt-2 font-heading font-black text-3xl tracking-tighter">{sede.city}</h3>
                                <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                                    <Info icon={MapPin} label="Indirizzo" value={`${sede.address}, ${sede.zip}${sede.cof ? " — " + sede.cof : ""}`} />
                                    <Info icon={Phone} label="Telefono" value={sede.phone} href={`tel:${sede.phone.replace(/\s/g, "")}`} />
                                    <Info icon={Mail} label="Email" value={sede.email} href={`mailto:${sede.email}`} />
                                    <Info icon={Clock3} label="Orari" value="Lu-Ve · 09:00–18:00" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Form preventivo */}
            <section id="preventivo" className="py-20 lg:py-28 bg-white">
                <div className="container-7m grid grid-cols-1 lg:grid-cols-12 gap-12">
                    <div className="lg:col-span-5">
                        <div className="label-eyebrow text-brand">Richiedi un preventivo</div>
                        <h2 className="mt-4 font-heading font-black text-4xl lg:text-5xl tracking-tighter leading-tight">
                            Parliamone.<br />Risposta in 24 ore.
                        </h2>
                        <p className="mt-6 text-ink-muted leading-relaxed">
                            Compila il form e ti risponderemo entro 24 ore con un preventivo personalizzato. Senza impegno, con la consulenza dei nostri esperti.
                        </p>
                        <ul className="mt-8 space-y-2 text-sm">
                            <li className="flex gap-3"><span className="text-brand">→</span>Preventivo gratuito e senza impegno</li>
                            <li className="flex gap-3"><span className="text-brand">→</span>Risposta garantita entro 24 ore</li>
                            <li className="flex gap-3"><span className="text-brand">→</span>Consulenza personalizzata inclusa</li>
                            <li className="flex gap-3"><span className="text-brand">→</span>Prezzi competitivi e trasparenti</li>
                        </ul>
                    </div>
                    <div className="lg:col-span-7 bg-[#FAFAFA] p-8 lg:p-12 border border-ink/10">
                        <QuoteForm />
                    </div>
                </div>
            </section>
        </>
    );
}

function Info({ icon: Icon, label, value, href }) {
    const content = (
        <div className="flex gap-3 items-start">
            <Icon className="w-4 h-4 text-brand mt-1 flex-shrink-0" strokeWidth={1.5} />
            <div>
                <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-ink-muted">{label}</div>
                <div className="font-medium mt-0.5">{value}</div>
            </div>
        </div>
    );
    return href ? <a href={href} className="hover:text-brand transition-colors">{content}</a> : content;
}
